import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  FileText, FileSignature, Loader2, Send, Mail, Trash2, Check, Clock, RefreshCw,
  MoreVertical, Bell, BellOff, Bookmark, Ban, AlertTriangle, ChevronRight, Lock,
  Eye, Download, X,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Active signature-requests tab — list of every PDF sent for e-sign, with
 * status filters, bulk archive, per-row actions (resend, save as template,
 * change reminder cadence, cancel with audit reason, view-only link toggle).
 *
 * Extracted from pages/SignaturesHub.jsx. State is local — only `requests`
 * (list), `loading`, and `onRefresh` (callback) come in via props. Drives
 * its own fetches for row-level mutations.
 */
export function RequestsTab({ requests, loading, onRefresh }) {
  const { currentWorkspace } = useWorkspace();
  const [resendingId, setResendingId] = useState(null);
  // P1 state: save-as-template prompt, cancel-with-reason prompt.
  const [tmplDialogReq, setTmplDialogReq] = useState(null);
  const [tmplName, setTmplName] = useState('');
  const [savingTmpl, setSavingTmpl] = useState(false);
  const [cancelDialogReq, setCancelDialogReq] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  // P2 state: status filter chips + bulk selection + view-only link toggling.
  const [statusFilter, setStatusFilter] = useState('all'); // all | sent | signed | cancelled | declined
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [viewLinkDialog, setViewLinkDialog] = useState(null);
  const [viewLinkUrl, setViewLinkUrl] = useState('');
  const [viewLinkBusy, setViewLinkBusy] = useState(false);
  // Inline PDF preview drawer — opened from the Eye button on each row.
  const [previewReq, setPreviewReq] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const openPreview = async (req) => {
    if (!currentWorkspace?.workspace_id) return;
    setPreviewReq(req);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/esign/requests/${req.request_id}/preview?workspace_id=${currentWorkspace.workspace_id}`,
      );
      if (r.ok) {
        setPreviewData(await r.json());
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not load PDF preview.');
        setPreviewReq(null);
      }
    } catch (e) {
      toast.error('Could not load PDF preview.');
      setPreviewReq(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewReq(null);
    setPreviewData(null);
  };

  const downloadPreview = () => {
    if (!previewData?.pdf_data_uri) return;
    const a = document.createElement('a');
    a.href = previewData.pdf_data_uri;
    const safeName = (previewData.subject || 'document')
      .replace(/[^a-z0-9-_ ]/gi, '').slice(0, 60).trim() || 'document';
    a.download = `${safeName}${previewData.is_signed ? ' (signed)' : ''}.pdf`;
    a.click();
  };

  // Counts per status — small chips next to the filter buttons.
  const statusCounts = useMemo(() => {
    const counts = { all: 0, sent: 0, signed: 0, cancelled: 0, declined: 0 };
    (requests || []).forEach((r) => {
      const s = r.status || 'sent';
      counts.all += 1;
      if (counts[s] !== undefined) counts[s] += 1;
    });
    return counts;
  }, [requests]);

  // Apply client-side filter + archived toggle to the list passed in.
  const filteredRequests = useMemo(() => {
    let list = requests || [];
    if (!showArchived) list = list.filter((r) => !r.archived);
    if (statusFilter !== 'all') list = list.filter((r) => (r.status || 'sent') === statusFilter);
    return list;
  }, [requests, statusFilter, showArchived]);

  const allSelected = filteredRequests.length > 0
    && filteredRequests.every((r) => selected.has(r.request_id));
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filteredRequests.map((r) => r.request_id)));
  };

  const handleBulkArchive = async () => {
    if (selected.size === 0) return;
    setBulkArchiving(true);
    try {
      const res = await apiFetch(
        `${API_URL}/api/esign/requests/bulk-archive?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ request_ids: Array.from(selected) }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to archive');
      toast.success(`Archived ${data.archived} request(s)`);
      setSelected(new Set());
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBulkArchiving(false);
    }
  };

  const handleViewOnlyToggle = async (req, enabled) => {
    setViewLinkBusy(true);
    try {
      const res = await apiFetch(
        `${API_URL}/api/esign/requests/${req.request_id}/view-only-link?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed');
      if (enabled) {
        setViewLinkUrl(data.view_url || '');
        toast.success('View-only link enabled');
      } else {
        setViewLinkUrl('');
        toast.success('View-only link disabled');
      }
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setViewLinkBusy(false);
    }
  };

  const handleResend = async (requestId) => {
    setResendingId(requestId);
    try {
      const res = await apiFetch(
        `${API_URL}/api/esign/requests/${requestId}/resend?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to resend');
      toast.success('Reminder email sent');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setResendingId(null);
    }
  };

  // P1: change auto-reminder cadence inline from the row dropdown.
  const handleCadence = async (requestId, cadence) => {
    try {
      const res = await apiFetch(
        `${API_URL}/api/esign/requests/${requestId}/reminder-cadence?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cadence }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to set cadence');
      toast.success(cadence === 'off' ? 'Auto-reminders off' : `Auto-reminders set to ${cadence.replace('every_', 'every ').replace('d', ' days')}`);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // P1: save the request's PDF + field placements as a reusable template.
  const handleSaveAsTemplate = async () => {
    if (!tmplDialogReq || !tmplName.trim()) return;
    setSavingTmpl(true);
    try {
      const res = await apiFetch(
        `${API_URL}/api/esign/requests/${tmplDialogReq.request_id}/save-as-template?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: tmplName.trim() }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to save template');
      toast.success(`Template saved: ${data.name}`);
      setTmplDialogReq(null);
      setTmplName('');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingTmpl(false);
    }
  };

  // P1: cancel with audit reason.
  const handleCancel = async () => {
    if (!cancelDialogReq || !cancelReason.trim()) return;
    setCancelling(true);
    try {
      const res = await apiFetch(
        `${API_URL}/api/esign/requests/${cancelDialogReq.request_id}/cancel?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: cancelReason.trim() }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to cancel');
      toast.success('Request cancelled');
      setCancelDialogReq(null);
      setCancelReason('');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }
  if (!requests.length) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-16 text-center text-muted-foreground">
          <FileSignature className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No signature requests yet. Use the "New Request" tab to send your first one.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <>
      {/* P2: status filter chips + archive toggle + bulk-action toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {[
          ['all', 'All', null],
          ['sent', 'Pending', Clock],
          ['signed', 'Signed', Check],
          ['cancelled', 'Cancelled', Ban],
          ['declined', 'Declined', AlertTriangle],
        ].map(([key, label, Icon]) => {
          const active = statusFilter === key;
          const count = statusCounts[key] || 0;
          return (
            <button
              key={key}
              onClick={() => { setStatusFilter(key); setSelected(new Set()); }}
              data-testid={`status-filter-${key}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                active
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white dark:bg-card border-border text-foreground hover:bg-accent'
              }`}
            >
              {Icon && <Icon className="w-3 h-3" />}
              {label}
              <span className={`ml-0.5 text-[10px] ${active ? 'opacity-80' : 'opacity-60'}`}>{count}</span>
            </button>
          );
        })}
        <button
          onClick={() => setShowArchived((v) => !v)}
          data-testid="toggle-show-archived"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ml-auto ${
            showArchived
              ? 'bg-zinc-700 text-white border-zinc-700'
              : 'bg-white dark:bg-card border-border text-muted-foreground hover:bg-accent'
          }`}
        >
          {showArchived ? 'Hide archived' : 'Show archived'}
        </button>
      </div>

      {/* Bulk-action bar — surfaces only when 1+ rows are checked */}
      {selected.size > 0 && (
        <div
          className="flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-950/30 text-xs"
          data-testid="bulk-action-bar"
        >
          <span><strong>{selected.size}</strong> selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleBulkArchive}
              disabled={bulkArchiving}
              className="bg-zinc-700 hover:bg-zinc-800 text-white gap-1.5"
              data-testid="bulk-archive-btn"
            >
              {bulkArchiving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bookmark className="w-3.5 h-3.5" />}
              Archive
            </Button>
          </div>
        </div>
      )}

      {/* Select-all row */}
      {filteredRequests.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-1 mb-1 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            className="rounded border-border"
            data-testid="select-all-toggle"
          />
          <span>Select all on this page</span>
        </div>
      )}

      <div className="space-y-2">
        {filteredRequests.length === 0 && (
          <p className="text-center text-xs text-muted-foreground py-8">
            {statusFilter === 'all' ? 'No requests yet.' : `No ${statusFilter} requests.`}
          </p>
        )}
        {filteredRequests.map(r => {
          const isClosed = r.status === 'signed' || r.status === 'cancelled' || r.status === 'declined';
          const cadence = r.reminder_cadence || 'off';
          const isSelected = selected.has(r.request_id);
          return (
            <Card key={r.request_id} data-testid={`request-row-${r.request_id}`} className={isSelected ? 'ring-2 ring-emerald-400' : ''}>
              <CardContent className="py-3 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelect(r.request_id)}
                  className="rounded border-border"
                  data-testid={`select-row-${r.request_id}`}
                />
                <FileSignature className={`w-5 h-5 flex-shrink-0 ${r.status === 'cancelled' || r.status === 'declined' ? 'text-zinc-400' : 'text-emerald-600'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${(r.status === 'cancelled' || r.status === 'declined') ? 'line-through text-muted-foreground' : ''}`}>{r.subject}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    <Mail className="inline w-3 h-3 mr-1" />
                    {(r.recipients?.length > 1) ? (
                      <span data-testid={`signer-chain-${r.request_id}`}>
                        {r.recipients.map((rc, i) => (
                          <span key={rc.recipient_id || i}>
                            {i > 0 && <span className="mx-1 text-zinc-400">→</span>}
                            <span className={
                              rc.status === 'signed'
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : (r.status === 'sent' && rc.order === r.current_signer_order
                                  ? 'text-amber-600 dark:text-amber-400 font-medium'
                                  : '')
                            }>
                              {rc.name || rc.email}{rc.status === 'signed' ? ' ✓' : ''}
                            </span>
                          </span>
                        ))}
                        <span className="ml-2 text-zinc-500">
                          {r.signed_count || 0}/{r.total_signers || r.recipients.length} signed
                        </span>
                      </span>
                    ) : (
                      <>
                        {r.recipient_email}
                        {r.recipient_name && ` · ${r.recipient_name}`}
                      </>
                    )}
                    {cadence !== 'off' && r.status === 'sent' && (
                      <span className="ml-2 inline-flex items-center gap-1 text-violet-600 dark:text-violet-400">
                        <Bell className="w-3 h-3" /> {cadence.replace('every_', 'every ').replace('d', 'd')}
                      </span>
                    )}
                    {r.archived && (
                      <span className="ml-2 inline-flex items-center gap-1 text-zinc-500">
                        <Bookmark className="w-3 h-3" /> archived
                      </span>
                    )}
                    {r.view_only_enabled && (
                      <span className="ml-2 inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                        <Lock className="w-3 h-3" /> view link
                      </span>
                    )}
                  </p>
                </div>
                <Badge
                  variant={r.status === 'signed' ? 'default' : 'outline'}
                  className={
                    r.status === 'signed'
                      ? 'bg-emerald-600'
                      : r.status === 'cancelled' || r.status === 'declined'
                        ? 'border-zinc-400 text-zinc-500'
                        : 'border-amber-400 text-amber-700 dark:text-amber-300'
                  }
                >
                  {r.status === 'signed' ? <Check className="w-3 h-3 mr-1" />
                    : r.status === 'cancelled' ? <Ban className="w-3 h-3 mr-1" />
                    : r.status === 'declined' ? <AlertTriangle className="w-3 h-3 mr-1" />
                    : <Clock className="w-3 h-3 mr-1" />}
                  {r.status}
                </Badge>
                <span className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">
                  {(r.signed_at || r.cancelled_at || r.sent_at || r.created_at)?.slice(0, 10)}
                </span>
                {/* Inline PDF preview — works for any status. Shows the
                    signed PDF when completed, else the original draft. */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openPreview(r)}
                  className="gap-1.5 h-8"
                  data-testid={`preview-${r.request_id}`}
                  title={r.status === 'signed' ? 'View signed PDF' : 'View PDF'}
                >
                  <Eye className="w-3.5 h-3.5" />
                  {r.status === 'signed' ? 'View signed' : 'View'}
                </Button>
                {r.status === 'sent' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResend(r.request_id)}
                    disabled={resendingId === r.request_id}
                    className="gap-1.5"
                    data-testid={`resend-${r.request_id}`}
                    title="Resend the signing email to the recipient"
                  >
                    {resendingId === r.request_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5" />
                    }
                    Resend
                  </Button>
                )}
                {/* P1: per-row actions dropdown — save as template / cadence / cancel.
                    P2: view-only link toggle is also surfaced here for any non-archived row. */}
                {!r.archived && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`row-actions-${r.request_id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {!isClosed && (
                        <>
                          <DropdownMenuItem
                            onClick={() => { setTmplDialogReq(r); setTmplName(`${r.subject || 'Template'}`); }}
                            data-testid={`save-as-tmpl-${r.request_id}`}
                          >
                            <Bookmark className="w-3.5 h-3.5 mr-2" /> Save as template…
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">Auto-reminders</div>
                          {[
                            ['off', 'Off (manual)', BellOff],
                            ['every_3d', 'Every 3 days', Bell],
                            ['every_7d', 'Every 7 days', Bell],
                            ['every_14d', 'Every 14 days', Bell],
                          ].map(([val, label, Icon]) => (
                            <DropdownMenuItem
                              key={val}
                              onClick={() => handleCadence(r.request_id, val)}
                              data-testid={`cadence-${val}-${r.request_id}`}
                            >
                              <Icon className="w-3.5 h-3.5 mr-2" /> {label}
                              {cadence === val && <Check className="w-3.5 h-3.5 ml-auto text-emerald-600" />}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem
                        onClick={() => {
                          setViewLinkDialog(r);
                          setViewLinkUrl(r.view_only_enabled && r.view_only_token
                            ? `${window.location.origin}/view/${r.view_only_token}` : '');
                        }}
                        data-testid={`view-link-${r.request_id}`}
                      >
                        <Lock className="w-3.5 h-3.5 mr-2" />
                        {r.view_only_enabled ? 'Manage view-only link' : 'Anyone with link can view…'}
                      </DropdownMenuItem>
                      {!isClosed && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => { setCancelDialogReq(r); setCancelReason(''); }}
                            className="text-red-600 focus:text-red-700"
                            data-testid={`cancel-req-${r.request_id}`}
                          >
                            <Ban className="w-3.5 h-3.5 mr-2" /> Cancel request…
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Save-as-template dialog */}
      <Dialog open={!!tmplDialogReq} onOpenChange={(o) => !o && setTmplDialogReq(null)}>
        <DialogContent data-testid="save-as-template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bookmark className="w-5 h-5 text-emerald-600" /> Save as template</DialogTitle>
            <DialogDescription>
              Snapshot this request's PDF + field placements as a reusable template. Recipient-specific info is stripped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="tmpl-name">Template name</Label>
            <Input
              id="tmpl-name"
              value={tmplName}
              onChange={(e) => setTmplName(e.target.value)}
              placeholder="Engagement Letter 2026"
              maxLength={120}
              data-testid="tmpl-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTmplDialogReq(null)}>Cancel</Button>
            <Button
              onClick={handleSaveAsTemplate}
              disabled={!tmplName.trim() || savingTmpl}
              data-testid="tmpl-save-confirm"
            >
              {savingTmpl ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Bookmark className="w-3.5 h-3.5 mr-1.5" />}
              Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel-with-reason dialog */}
      <Dialog open={!!cancelDialogReq} onOpenChange={(o) => !o && setCancelDialogReq(null)}>
        <DialogContent data-testid="cancel-request-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /> Cancel signature request</DialogTitle>
            <DialogDescription>
              The signing link will stop working immediately. Future auto-reminders for this request will also stop.
              The reason is recorded in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Reason (required)</Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Replaced with newer terms; client withdrew; sent to wrong person"
              maxLength={300}
              rows={3}
              data-testid="cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelDialogReq(null)}>Never mind</Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={!cancelReason.trim() || cancelling}
              data-testid="cancel-confirm-btn"
            >
              {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Ban className="w-3.5 h-3.5 mr-1.5" />}
              Cancel request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* P2: View-only link toggle + copy dialog */}
      <Dialog open={!!viewLinkDialog} onOpenChange={(o) => !o && setViewLinkDialog(null)}>
        <DialogContent data-testid="view-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Lock className="w-5 h-5" /> Anyone with link can view
            </DialogTitle>
            <DialogDescription>
              Generate a read-only public link to share the {viewLinkDialog?.status === 'signed' ? 'signed' : 'current'} PDF with stakeholders who don't need to sign. The link can be revoked at any time.
            </DialogDescription>
          </DialogHeader>
          {viewLinkDialog?.view_only_enabled || viewLinkUrl ? (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Share link</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={viewLinkUrl}
                    className="font-mono text-xs"
                    data-testid="view-link-url"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard?.writeText(viewLinkUrl);
                      toast.success('Link copied');
                    }}
                    data-testid="view-link-copy"
                  >
                    Copy
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Anyone with this URL can view the PDF without logging in. Don't share with people who shouldn't see it.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">
              Enable the link to generate a share URL. No one can access it until enabled.
            </p>
          )}
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {viewLinkDialog?.view_only_enabled || viewLinkUrl ? (
              <Button
                variant="ghost"
                onClick={() => handleViewOnlyToggle(viewLinkDialog, false).then(() => setViewLinkDialog(null))}
                disabled={viewLinkBusy}
                className="text-red-600 hover:text-red-700"
                data-testid="view-link-revoke"
              >
                {viewLinkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Ban className="w-3.5 h-3.5 mr-1.5" />}
                Revoke link
              </Button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setViewLinkDialog(null)}>Close</Button>
              {!viewLinkDialog?.view_only_enabled && !viewLinkUrl && (
                <Button
                  onClick={() => handleViewOnlyToggle(viewLinkDialog, true)}
                  disabled={viewLinkBusy}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="view-link-enable"
                >
                  {viewLinkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}
                  Enable link
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline PDF preview drawer — opens from the Eye button on each row.
          Side-sheet keeps the user in the Signatures Hub context (no new
          tab, no full-page navigation). */}
      <Sheet open={!!previewReq} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-4xl flex flex-col p-0"
          data-testid="signature-preview-drawer"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <SheetTitle className="truncate">
                  {previewReq?.subject || 'Document preview'}
                </SheetTitle>
                <SheetDescription className="flex items-center gap-2 mt-1">
                  <span className="truncate">{previewReq?.recipient_name}</span>
                  {previewData?.is_signed && (
                    <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-[10px]">
                      <Check className="w-2.5 h-2.5 mr-1" /> Signed
                    </Badge>
                  )}
                  {previewData?.signed_at && (
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(previewData.signed_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      })}
                    </span>
                  )}
                </SheetDescription>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm" variant="outline"
                  onClick={downloadPreview}
                  disabled={!previewData?.pdf_data_uri}
                  data-testid="signature-preview-download"
                  className="gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={closePreview}
                  data-testid="signature-preview-close"
                  className="h-8 w-8 p-0"
                  aria-label="Close preview"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-hidden bg-zinc-100 dark:bg-zinc-900">
            {previewLoading ? (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : previewData?.pdf_data_uri ? (
              // Native browser PDF viewer via <iframe>. Works in all modern
              // browsers — no external pdf.js dependency, no rendering lag.
              <iframe
                title="Signed document preview"
                src={previewData.pdf_data_uri}
                className="w-full h-full border-0"
                data-testid="signature-preview-iframe"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                No PDF data available.
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────


export default RequestsTab;
